import React from 'react';
import { Link } from 'react-router-dom';
import CTASection from '@/components/CTASection';
import ServiceCard from '@/components/ServiceCard';
import TestimonialCard from '@/components/TestimonialCard';
import LeadgeneratieCTA from '@/components/LeadgeneratieCTA';
import CalendlyInline from '@/components/CalendlyInline';
import ClientLogoStrip from '@/components/home/ClientLogoStrip';
import FivePillarsSection from '@/components/home/FivePillarsSection';
import GuaranteesSection from '@/components/home/GuaranteesSection';
import SectorFocusSection from '@/components/home/SectorFocusSection';
import ProductenTeaser from '@/components/home/ProductenTeaser';
import { PhoneCall, Calendar, BarChart3, ArrowRight, Clock, Trophy, Rocket, PhoneIncoming, CheckCircle2, Shield, Target, Users } from 'lucide-react';
import PageSEO from '@/components/seo/PageSEO';
const structuredData = [{
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://afsprakenmaker.be/#organization",
  "name": "Afsprakenmaker.be",
  "alternateName": "B2B Sales Outsourcing België",
  "url": "https://afsprakenmaker.be",
  "logo": "https://afsprakenmaker.be/logo-2026.png",
  "description": "B2B sales outsourcing partner. 15 jaar ervaring, +500 projecten, 12 landen. Consistency Wins. Voor IT, SaaS, Consultancy, HR en Finance bedrijven met 25-500+ medewerkers.",
  "sameAs": ["https://www.facebook.com/afsprakenmaker", "https://www.linkedin.com/company/afsprakenmaker", "https://www.instagram.com/afsprakenmaker.be"],
  "areaServed": {
    "@type": "Country",
    "name": "Belgium"
  }
}, {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [{
    "@type": "Question",
    "name": "Wat doet Afsprakenmaker precies?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": "Afsprakenmaker is jouw B2B sales outsourcing partner. Wij nemen de volledige prospectie, opvolging en pipeline-management uit handen. Geen intern SDR-team opzetten — wij leveren voorspelbare pipeline. Consistency Wins."
    }
  }, {
    "@type": "Question",
    "name": "Voor welke bedrijven werkt Afsprakenmaker?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": "We werken met B2B-organisaties van 25 tot 500+ medewerkers met een actieve salesorganisatie. Focus sectoren: IT & SaaS, Consultancy, HR & Recruitment, Finance & Insurance, Technologie en B2B Services."
    }
  }, {
    "@type": "Question",
    "name": "Hoe werkt Afsprakenmaker?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": "Wij werken volgens het principe Consistency Wins: respectvol contact dat je database beschermt, gekwalificeerde afspraken met mensen die komen opdagen, en transparante rapportage zodat je weet wat je kan verwachten."
    }
  }, {
    "@type": "Question",
    "name": "Hoe snel zie ik resultaten?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": "Direct productief vanaf dag 1. Eerste afspraken binnen 2-3 weken. Onze langdurige klantrelaties spreken voor zich — resultaat volgt uit consequente uitvoering."
    }
  }]
}];
const Index = () => {
  return <div className="min-h-screen flex flex-col overflow-x-hidden">
      <PageSEO title="Afsprakenmaker.be | B2B Sales Outsourcing België | Consistency Wins" description="B2B sales outsourcing partner. 15 jaar ervaring, +500 projecten, 12 landen. Consistency Wins — resultaat volgt uit consequente uitvoering." keywords="B2B sales outsourcing, sales outsourcing België, SDR outsourcing, pipeline management, leadgeneratie B2B, afspraken maken, prospectie uitbesteden" canonicalUrl="/" structuredData={structuredData} />
      
      {/* Hero Section - Institutional, symmetric */}
      <section className="bg-brand-lightBlue pt-32 pb-12 md:pt-36 md:pb-16">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                Jouw targets worden onze targets.
              </h1>
              <p className="text-xl text-gray-700 mb-3">
                Eén partner, vijf functies. Een machine die werkt en waar je op kan vertrouwen.
              </p>
              <p className="text-sm text-gray-500 mb-8">
                15 jaar · 500+ projecten · 12 landen
              </p>

              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-blue mt-1 flex-shrink-0" />
                  <p className="text-gray-700">Geen intern SDR-team nodig. Wij regelen alles.</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-blue mt-1 flex-shrink-0" />
                  <p className="text-gray-700">Voorspelbare pipeline, zonder trial-and-error op jouw markt.</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-blue mt-1 flex-shrink-0" />
                  <p className="text-gray-700">Respectvol contact. Wij beschermen je database.</p>
                </div>
              </div>

              <div>
                <p className="text-lg font-bold text-brand-blue tracking-wide">
                  CONSISTENCY WINS.
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Resultaat volgt uit consequente uitvoering.
                </p>
              </div>
            </div>

            <div>
              <CalendlyInline
                url="https://calendly.com/bert-lambrecht-am/dm?hide_gdpr_banner=1&primary_color=8B1A1A&text_color=111111"
                className="w-full"
                showHeading={false}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Client Logo Strip */}
      <ClientLogoStrip />

      {/* Five Pillars Section */}
      <FivePillarsSection />

      {/* Guarantees Section */}
      <GuaranteesSection />

      {/* Sector Focus Section */}
      <SectorFocusSection />

      {/* Services Section */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">Wat we voor je doen</h2>
            <p className="section-subtitle mx-auto">
              Van eerste contact tot concrete afspraak. Volledig geïntegreerd in jouw systemen.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <ServiceCard title="Telefonische prospectie" description="Dagelijkse outbound prospectie met focus op kwaliteit. Continue flow van gesprekken met jouw ideale klanten." icon={<PhoneCall size={32} />} link="/diensten#outbound" />
            <ServiceCard title="Afspraken inplannen" description="Van eerste contact tot concrete afspraak. Professioneel en afgestemd op jouw beschikbaarheid." icon={<Calendar size={32} />} link="/diensten#outbound" />
            <ServiceCard title="CRM-integratie" description="Real-time pipeline-inzicht. Alle interacties en voortgang direct zichtbaar in jouw systemen." icon={<BarChart3 size={32} />} link="/diensten#outbound" />
            <ServiceCard title="Inbound Afspraken" description="AI-gedreven en persoonlijke inbound afhandeling. Elk contact maximaal benut." icon={<PhoneIncoming size={32} />} link="/diensten#inbound" />
          </div>
          
          <div className="mt-10 text-center">
            <Link to="/diensten" className="inline-flex items-center gap-2 text-brand-blue hover:underline font-medium">
              Bekijk al onze diensten
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="bg-brand-gray section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">Hoe we jouw commerciële motor bouwen</h2>
            <p className="section-subtitle mx-auto">
              Een gestructureerde aanpak die voorspelbaarheid, ritme en continue verbetering combineert.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 items-center gap-8">
            <div className="order-2 md:order-1 pr-0 md:pr-8">
              <div className="space-y-8">
                <div>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="bg-brand-blue text-white rounded-full h-8 w-8 flex items-center justify-center font-bold">1</div>
                    <h3 className="text-xl font-semibold">We analyseren jouw commerciële DNA</h3>
                  </div>
                  <p className="text-gray-600 pl-12">
                    Diepgaande analyse van jouw doelgroep, salesproces en concurrentiepositie. We bouwen een scherp beeld van wat werkt.
                  </p>
                </div>
                
                <div>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="bg-brand-blue text-white rounded-full h-8 w-8 flex items-center justify-center font-bold">2</div>
                    <h3 className="text-xl font-semibold">We ontwerpen jouw commerciële motor</h3>
                  </div>
                  <p className="text-gray-600 pl-12">
                    Van targeting tot messaging: een systeem dat schaalbaar is en meetbare resultaten levert. Met concrete KPI's.
                  </p>
                </div>
                
                <div>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="bg-brand-blue text-white rounded-full h-8 w-8 flex items-center justify-center font-bold">3</div>
                    <h3 className="text-xl font-semibold">We draaien de motor op toeren</h3>
                  </div>
                  <p className="text-gray-600 pl-12">
                    Dagelijkse prospectie, opvolging en pipeline-management. Onze teams werken als verlengstuk van jouw organisatie.
                  </p>
                </div>
                
                <div>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="bg-brand-blue text-white rounded-full h-8 w-8 flex items-center justify-center font-bold">4</div>
                    <h3 className="text-xl font-semibold">Jij oogst voorspelbare groei</h3>
                  </div>
                  <p className="text-gray-600 pl-12">
                    Gekwalificeerde afspraken, real-time pipeline-inzicht en kwartaal Growth Council voor strategische bijsturing.
                  </p>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <img src="https://images.unsplash.com/photo-1556761175-4b46a572b786?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=700&q=75" alt="Team bespreken groeistrategie" className="rounded-lg shadow-md border border-gray-100" width={700} height={467} loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">Voorspelbare impact, meetbare groei</h2>
            <p className="section-subtitle mx-auto">
              Onze klanten ervaren structurele groei door het ritme en de discipline van een professionele commerciële motor.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="p-6">
              <div className="text-3xl md:text-4xl font-bold text-brand-blue mb-2">15+</div>
              <p className="text-gray-700">Jaar ervaring in B2B sales</p>
            </div>
            
            <div className="p-6">
              <div className="text-3xl md:text-4xl font-bold text-brand-blue mb-2">500+</div>
              <p className="text-gray-700">Succesvolle projecten</p>
            </div>
            
            <div className="p-6">
              <div className="text-3xl md:text-4xl font-bold text-brand-blue mb-2">12</div>
              <p className="text-gray-700">Landen actief</p>
            </div>
            
            <div className="p-6">
              <div className="text-3xl md:text-4xl font-bold text-brand-blue mb-2">∞</div>
              <p className="text-gray-700">Langdurige partnerships</p>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility Strip */}
      <section className="py-16 bg-brand-lightBlue border-t border-gray-100">
        <div className="container">
          <div className="text-center mb-10">
            <h2 className="section-title">Waarom groeigerichte organisaties voor ons kiezen</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="flex flex-col items-center p-6 bg-white rounded-lg text-center shadow-sm">
              <div className="text-brand-blue mb-4">
                <Clock size={36} />
              </div>
              <p className="font-medium text-lg">15 jaar expertise in B2B sales outsourcing</p>
            </div>
            
            <div className="flex flex-col items-center p-6 bg-white rounded-lg text-center shadow-sm">
              <div className="text-brand-blue mb-4">
                <Trophy size={36} />
              </div>
              <p className="font-medium text-lg">Genomineerd als Trends Gazellen-bedrijf</p>
            </div>
            
            <div className="flex flex-col items-center p-6 bg-white rounded-lg text-center shadow-sm">
              <div className="text-brand-blue mb-4">
                <Rocket size={36} />
              </div>
              <p className="font-medium text-lg">Gebouwd door ondernemers, voor ondernemers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Enhanced with result-driven cases */}
      <section className="bg-white section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">Wat onze klanten zeggen</h2>
            <p className="section-subtitle mx-auto">
              Resultaten die spreken. Van startup naar marktleider, van chaos naar voorspelbaarheid.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <TestimonialCard author="Werner Temmerman" role="Sales Director Belgium" company="Penneo" quote="We hebben ons marktaandeel vergroot en ons vermogen om deals te sluiten is in korte tijd aanzienlijk verbeterd. Van startup naar marktleider." />
            <TestimonialCard author="Frank Prinsen" role="Manager klantrelaties" company="Cipal Schaubroeck" quote="Politiek is chaos. Jullie maken het voorspelbaar. Afsprakenmaker heeft onze best practices voor lead- en dealgeneratie volledig getransformeerd." />
            <TestimonialCard author="Koen Van Riet" role="Sales Director" company="Cleaning Masters" quote="Structureel partnership sinds 2018. De consistentie en het ritme dat Afsprakenmaker brengt is ongeëvenaard in de markt." />
          </div>
          
          <div className="mt-10 text-center">
            <Link to="/leadgeneratie-kmo" className="btn-primary">
              Meer succesverhalen bekijken
            </Link>
          </div>
        </div>
      </section>

      {/* Lead Generation CTA */}
      

      {/* Final CTA Section */}
      <ProductenTeaser />
      <CTASection />
    </div>;
};
export default Index;